import React, { useState, useEffect } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'

function SelectFiles(props) {
    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [selectedJobFileId, setSelectedJobFileId] = useState('')
    const [jobFiles, setJobFiles] = useState([])
    const [filesWithinZip, setFilesWithinZip] = useState([])
    const [individualFiles, setIndividualFiles] = useState([])
    const [idSourceFiles, setIdSourceFiles] = useState([])
    const [usingFlatFiles, setUsingFlatFiles] = useState(false)
    const [usingExcel, setUsingExcel] = useState(false)
    const [textQualifier, setTextQualifier] = useState(-1)
    const [textDelimiter, setTextDelimiter] = useState(0)
    const [customDelimiter, setCustomDelimiter] = useState('')
    const [availableExcelTables, setAvailableExcelTables] = useState([])
    const [selectedExcelTables, setSelectedExcelTables] = useState([])
    const [importMode, setImportMode] = useState('flat-files')

    // trigger on component mount
    useEffect(() => {
        if (props.jobFiles && props.jobFiles.length > 0) {
            setJobFiles(props.jobFiles)
            filterAndSetIndividualFiles(props.jobFiles)
        }
    }, [])

    useEffect(() => {
        if (props.jobFiles && props.jobFiles.length > 0 && jobFiles.length !== props.jobFiles.length) {
            setJobFiles(props.jobFiles)
            filterAndSetIndividualFiles(props.jobFiles)
        }
    }, [props.jobFiles])

    function handleWizardFileChange(event) {
        event.preventDefault()
        zipFileSelected(event.target.value)
    }

    function zipFileSelected(zipFileName) {
        setSelectedJobFileId(zipFileName)

        let lookup = null
        for (let x = 0; x < jobFiles.length; x++) {
            if (jobFiles[x]["identifier"] === zipFileName) {
                lookup = jobFiles[x]
            }
        }

        if (lookup) {
            setFilesWithinZip(processFilesWithinZipSolo(lookup["zipIndividualFiles"]))
            fireOnSaveEvent(zipFileName, textQualifier, idSourceFiles, textDelimiter, customDelimiter, selectedExcelTables)
        } else {
            setFilesWithinZip([])
        }
    }

    function processFilesWithinZipSolo(zippedFiles) {
        let rtnVal = []

        for (let x = 0; x < zippedFiles.length; x++) {
            rtnVal.push({"id": zippedFiles[x], "name": zippedFiles[x]})
        }

        return rtnVal
    }

    /**
     * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {Array} jobFilesFromProps - All of the job files we got from Props
     */
    function filterAndSetIndividualFiles(jobFilesFromProps) {
        let processedArray = []

        for (let x = 0; x < jobFilesFromProps.length; x++) {
            if (!jobFilesFromProps[x]["name"].toLowerCase().includes(".zip")) {
                processedArray.push({ "id": jobFilesFromProps[x]["identifier"], "name": jobFilesFromProps[x]["name"] })
            }
        }

        setIndividualFiles(processedArray)
    }

    function filterOutFlatFiles(files) {
        let rtnVal = []
        for (let x = 0; x < files.length; x++) {
            if (files[x]["name"].toLowerCase().includes(".csv") || files[x]["name"].toLowerCase().includes(".txt") || files[x]["name"].toLowerCase().includes(".xls") || files[x]["name"].toLowerCase().includes(".xlsx")) {
                rtnVal.push(files[x])
            }
        }
        return rtnVal
    }

    /**
     * If flat files, render this area, otherwise, it'll render [renderExcelTableArea()]
     */
    function renderTextFileInfoArea() {
        if (usingFlatFiles) {
            return (<>
                <FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
                    <InputLabel id="text-delimiter-label">Choose File Delimiter</InputLabel>
                    <Select
                        labelId="text-delimiter-label"
                        id="text-delimiter"
                        label="Choose File Delimiter"
                        onChange={(e) => {
                            e.preventDefault()
                            setTextDelimiter(e.target.value)
                            fireOnSaveEvent(selectedJobFileId, textQualifier, idSourceFiles, e.target.value, customDelimiter, selectedExcelTables)
                        }}
                        value={textDelimiter}
                        defaultValue={0}
                        required
                        fullWidth
                        disabled={isLoadingRequest}>
                            <MenuItem value={0}>Comma</MenuItem>
                            <MenuItem value={1}>Tab</MenuItem>
                            <MenuItem value={2}>Space</MenuItem>
                            <MenuItem value={3}>Semicolon</MenuItem>
                            <MenuItem value={4}>Custom</MenuItem>
                    </Select>
                </FormControl>
                {renderCustomDelimArea(textDelimiter)}
                <FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
                    <InputLabel id="text-qualifier-label">Choose Text Qualifier</InputLabel>
                    <Select
                        labelId="text-qualifier-label"
                        id="text-qualifier"
                        label="Choose Text Qualifier"
                        onChange={(e) => {
                            e.preventDefault()
                            setTextQualifier(e.target.value)
                            fireOnSaveEvent(selectedJobFileId, e.target.value, idSourceFiles, textDelimiter, customDelimiter, selectedExcelTables)
                        }}
                        value={textQualifier}
                        defaultValue={-1}
                        required
                        fullWidth
                        disabled={isLoadingRequest}>
                            <MenuItem value={-1}>Choose Text Qualifier</MenuItem>
                            <MenuItem value={0}>None</MenuItem>
                            <MenuItem value={1}>Single Quote</MenuItem>
                            <MenuItem value={2}>Double Quote</MenuItem>
                    </Select>
                </FormControl>
            </>)
        }

        return null
    }

    function renderCustomDelimArea(delimType) {
        if (delimType === 4) {
            return <FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
                <TextField
                    fullWidth
                    label="Custom Delimiter"
                    variant="outlined"
                    value={customDelimiter}
                    disabled={isLoadingRequest}
                    onChange={(e) => {
                        e.preventDefault()
                        setCustomDelimiter(e.target.value)
                        fireOnSaveEvent(selectedJobFileId, textQualifier, idSourceFiles, textDelimiter, e.target.value, selectedExcelTables)
                }} />
            </FormControl>
        }
        
        return null
    }

    /**
     * Reads the state variables to determine if we need to render the excel drop down
     */
    function renderExcelMultipleTableAreas() {
        if (usingExcel) {
            return (<FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
                <Autocomplete
                    multiple
                    id="excel-sheets-standard"
                    options={availableExcelTables}
                    getOptionLabel={(option) => option}
                    value={selectedExcelTables}
                    disabled={isLoadingRequest}
                    onChange={(event, newValue) => {
                        setSelectedExcelTables([ ...newValue ])
                        fireOnSaveEvent(selectedJobFileId, textQualifier, idSourceFiles, textDelimiter, customDelimiter, newValue)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            label="Sheets From Excel"
                            placeholder="Sheets From Excel"
                        />
                    )} />
            </FormControl>)
        }

        return null
    }

    /**
     * getExcelTablesFromFiles
     * * Call the API end point to get the tables from all of the excel files
     * @param {Array} fileNames - File details of what was selected in the UI
    */
    function getExcelTablesFromFiles(fileNames) {
        let fileBlobs = []
        
        // First, look up file names within a given zip file
        for (let x = 0; x < fileNames.length; x++) {
            let lookup = null

            // Are we pulling a file from a zip?
            if (fileNames[x]["id"] === fileNames[x]["name"]) {
                lookup = jobFiles.filter(jobFile => jobFile["identifier"] === selectedJobFileId)
                
                for (let x = 0; x < lookup.length; x++) {
                    fileBlobs.push({
                        name: lookup[x]["name"],
                        url: lookup[x]["url"],
                        containerName: lookup[x]["containerName"],
                        blobName: lookup[x]["blobName"],
                        selectedFileName: fileNames[x]["id"],
                        identifier: lookup[x]["identifier"]
                    })
                }
            } else {
                // This is a standalone file not within a zip
                lookup = jobFiles.filter(jobFile => jobFile["identifier"] === fileNames[x]["id"])
                
                for (let x = 0; x < lookup.length; x++) {
                    fileBlobs.push({
                        name: lookup[x]["name"],
                        url: lookup[x]["url"],
                        containerName: lookup[x]["containerName"],
                        blobName: lookup[x]["blobName"],
                        selectedFileName: fileNames[x]["id"],
                        identifier: lookup[x]["identifier"]
                    })
                }
            }
        }
        
        if (fileBlobs.length > 0) {
            setIsLoadingRequest(true)
            let getExcelTablesReq = props.readTablesFromExcelFiles(fileBlobs)
            getExcelTablesReq.then((resp) => { 
                if (resp.status !== 200) {
                    throw new Error(resp)
                } else {
                    return resp.json()
                }
            }).then((json) => {
                setIsLoadingRequest(false)
                setAvailableExcelTables(json)
            }).catch((ex) => {
                console.error(ex)
                setIsLoadingRequest(false)
            })
        } else {
            setIsLoadingRequest(false)
            setAvailableExcelTables([])
        }
    }

    /**
     * fireOnSaveEvent
     * * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {string} zipId - GUID for the zip file if one is used
     * @param {int} qualifier - Text qualifier
     * @param {Array} files - Array of GUIDs and file names
     * @param {int} delimiter - Text delimiter
     * @param {string} manualTextDelimiter - Custom entry for a delimiter
     * @param {string[]} excelTables - All selected excel tables
     */
    function fireOnSaveEvent(zipId, qualifier, files, delimiter, manualTextDelimiter, excelTables) {
        if (props.onSave) {
            props.onSave({ "zipFileId": zipId, "textQualifier": qualifier, "sourceFiles": files, "textDelimiter": delimiter, "customDelimiter": manualTextDelimiter, "selectedExcelTables": excelTables })
        }
    }

    return <>
        <FormGroup>
            <RadioGroup
                row
                defaultValue="flat-files"
                name="radio-buttons-group"
                onChange={(e) => {
                    setImportMode(e.target.value)
                }}
            >
                <FormControlLabel value="flat-files" control={<Radio checked={importMode==='flat-files'} />} label="Flat File(s) (Text and Excel)" />
                <FormControlLabel value="sql-db" control={<Radio disabled checked={importMode==='sql-db'} />} label="SQL Server" />
            </RadioGroup>
        </FormGroup>
        <FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
            <InputLabel id="select-zip-file-label">Select Zip File</InputLabel>
            <Select
                labelId="select-zip-file-label"
                id="select-zip-file"
                label="Select Zip File"
                onChange={handleWizardFileChange}
                value={selectedJobFileId}
                defaultValue={''}
                required
                fullWidth>
                    <MenuItem value={''}>Select Zip File</MenuItem>
                    { jobFiles.map((x) => x && x.name.toLowerCase().includes(".zip") && <MenuItem key={x.identifier} value={x.identifier}>{x.name}</MenuItem>) }
            </Select>
        </FormControl>
        <FormControl className="field" fullWidth style={{ 'marginTop': '25px' }}>
            <Autocomplete
                multiple
                id="source-files-standard"
                options={filterOutFlatFiles(filesWithinZip.concat(individualFiles))}
                getOptionLabel={(option) => option.name}
                value={idSourceFiles}
                disabled={isLoadingRequest}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    let isThereAnyExcelFiles = false
                    let isThereAnyFlatFiles = false
                    setUsingExcel(false)
                    setUsingFlatFiles(false)
                    
                    for (let fileItr = 0; fileItr < newValue.length; fileItr++) {
                        if (newValue[fileItr]["name"].toLowerCase().includes(".xlsx") || newValue[fileItr]["name"].toLowerCase().includes(".xls")) {
                            isThereAnyExcelFiles = true
                            setUsingExcel(true)
                        } else {
                            isThereAnyFlatFiles = true
                            setUsingFlatFiles(true)
                        }
                    }
                    
                    if (isThereAnyExcelFiles && isThereAnyFlatFiles) {
                        setIdSourceFiles([])
                        setUsingExcel(false)
                        setUsingFlatFiles(false)
                    } else {
                        // Read the excel file (assume its the very first file for now), get the list of tabs associated with the file
                        if (newValue.length > 0 && isThereAnyExcelFiles) {
                            getExcelTablesFromFiles(filterOutFlatFiles(newValue))
                        }
                        setIdSourceFiles([
                            ...newValue
                        ])
                        fireOnSaveEvent(selectedJobFileId, textQualifier, newValue, textDelimiter, customDelimiter, selectedExcelTables)
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        label="Source Files"
                        placeholder="Source Files"
                    />
                )} />
        </FormControl>
        {renderTextFileInfoArea()}
        {renderExcelMultipleTableAreas()}
    </>
}

export default SelectFiles